import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FaPaperPlane, FaDownload, FaGraduationCap, FaLaptopCode, FaBrain, FaCode, FaPlay, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import reconnu from './reconnu3.png';
import YouTube from 'react-youtube';
import './Bac5ISITN.css';

// Hook personnalisé pour détecter la taille de l'écran
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, [matches, query]);

  return matches;
};

export default function Bac5ISITN() {
  const [showVideo, setShowVideo] = useState(false);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);
  const isMobile = useMediaQuery('(max-width: 850px)');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCandidaterClick = () => {
    navigate('/PREINSCRIPTION');
  };

  const handleDownloadClick = () => {
    window.open('images/final/ISITN.pdf', '_blank');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 1,
      controls: 1,
      modestbranding: 1,
      rel: 0
    },
  };

  const videoId = isMobile ? 'rGriJZ3Gp4o' : 'Y2xnT7z6aiE';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.5 }
  };

  const videoModalVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.3
      }
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <>
      <motion.div 
        className="isitn-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
      
        <motion.div 
          className="isitn-header" 
          variants={itemVariants}
          style={{ opacity, scale }}
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <FaGraduationCap size={60} color="#4cb6ea" />
          </motion.div>
          <h1 className="isitn-title">
            INGÉNIERIE DES SYSTÈMES D'INFORMATION APPLIQUÉE À LA TRANSFORMATION NUMÉRIQUE (ISITN)
          </h1>
          <h2 className="isitn-subtitle">Master en Ingénierie des Systèmes d'Information & Transformation Numérique</h2>
        </motion.div>

        <motion.div className="isitn-content" variants={itemVariants}>
          <motion.p className="program-description" variants={itemVariants}>
            Formation d'excellence reconnue par l'État, notre Master en Ingénierie des Systèmes d'Information et Transformation Numérique (ISITN) est un diplôme prestigieux qui prépare les futurs leaders de la transformation numérique. Cette formation innovante de deux ans combine expertise technique et vision stratégique, permettant aux étudiants de maîtriser les technologies émergentes et les méthodologies de transformation digitale. Le programme est disponible en formation initiale ou en alternance, offrant une expérience professionnelle enrichissante avec 2 jours en entreprise et 3 jours à l'école.
          </motion.p>

          <motion.div className="program-features" variants={itemVariants}>
            <div className="feature-grid">
              <motion.div 
                className="feature-item"
                onClick={() => setShowVideo(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ cursor: 'pointer' }}
              >
                <FaGraduationCap size={30} color="#4cb6ea" />
                <h4>
                  Possibilité de Formation en Alternance
                  <FaPlay size={15} style={{ marginLeft: '10px', verticalAlign: 'middle' }} />
                </h4>
                <p>2 jours en entreprise (Lundi-Mardi) et 4 jours à l'école (Mercredi-Samedi) pour une expérience professionnelle enrichissante</p>
              </motion.div>
              <div className="feature-item">
                <FaCode size={30} color="#4cb6ea" />
                <h4>Expertise Digitale</h4>
                <p>Maîtrise des technologies de transformation numérique et des méthodologies agiles</p>
              </div>
              <div className="feature-item">
                <FaBrain size={30} color="#4cb6ea" />
                <h4>Vision Stratégique</h4>
                <p>Développement de compétences en leadership et gestion de projets digitaux</p>
              </div>
            </div>
          </motion.div>

          <motion.div className="program-opportunities" variants={itemVariants}>
            <h3 className="opportunities-title">Débouchés d'Excellence</h3>
            <div className="opportunities-grid">
              <div className="opportunity-block">
                <h4>Carrières Prometteuses</h4>
                <ul>
                  <li>Directeur de la Transformation Numérique</li>
                  <li>Architecte Solutions Digitales</li>
                  <li>Chef de Projet Innovation Digitale</li>
                  <li>Consultant en Transformation Digitale</li>
                  <li>Responsable des Systèmes d'Information</li>
                </ul>
              </div>
              <div className="opportunity-block">
                <h4>Expertise Acquise</h4>
                <ul>
                  <li>Transformation Digitale des Entreprises</li>
                  <li>Gouvernance des Systèmes d'Information</li>
                  <li>Innovation et Technologies Émergentes</li>
                  <li>Gestion de Projets Agiles</li>
                  <li>Cybersécurité et Protection des Données</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div className="semester-section" variants={cardVariants}>
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <FaCode size={40} color="#4cb6ea" style={{ marginBottom: '1rem' }} />
            </motion.div>
            <h3 className="semester-title">1er Semestre</h3>
            <ul className="course-list">
              <motion.li className="course-item" variants={itemVariants}>ISITN S1.1 - Génie Logiciel & Ingénierie du Web</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S1.2 - Marketing Digital</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S1.3 - IA et IoT pour le Web</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S1.4 - Bases de données Avancées</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S1.5 - Design Thinking & Business Plan</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S1.6 - Culture et Communication</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S1.7 - Power Skills (Développement personnel)</motion.li>
            </ul>
          </motion.div>

          <motion.div className="semester-section" variants={cardVariants}>
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <FaBrain size={40} color="#4cb6ea" style={{ marginBottom: '1rem' }} />
            </motion.div>
            <h3 className="semester-title">2ème Semestre</h3>
            <ul className="course-list">
              <motion.li className="course-item" variants={itemVariants}>ISITN S2.1 - Cadrage d'un Projet SI</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S2.2 - Gestion et Planification de projet agile</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S2.3 - Composants Logiciels pour l'Entreprise</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S2.4 - Management de Projet Informatique</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S2.5 - Big Data et Data Mining</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S2.6 - Culture et Communication 2</motion.li>
            </ul>
          </motion.div>

          <motion.div className="semester-section" variants={cardVariants}>
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <FaLaptopCode size={40} color="#4cb6ea" style={{ marginBottom: '1rem' }} />
            </motion.div>
            <h3 className="semester-title">3ème Semestre</h3>
            <ul className="course-list">
              <motion.li className="course-item" variants={itemVariants}>ISITN S3.1 - Transformation Digitale & Innovation</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S3.2 - Blockchain & Smart Contracts</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S3.3 - Cloud Computing & DevOps</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S3.4 - Cybersécurité & RGPD</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S3.5 - Projet de Synthèse</motion.li>
              <motion.li className="course-item" variants={itemVariants}>ISITN S3.6 - Culture et Communication 3</motion.li>
            </ul>
          </motion.div>

          <motion.div className="semester-section" variants={cardVariants}>
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <FaGraduationCap size={40} color="#4cb6ea" style={{ marginBottom: '1rem' }} />
            </motion.div>
            <h3 className="semester-title">4ème Semestre</h3>
            <ul className="course-list">
              <motion.li className="course-item" variants={itemVariants}>ISITN S4 - Stage de Fin d'Études</motion.li>
            </ul>
          </motion.div>

          <motion.div 
            className="action-buttons" 
            variants={itemVariants}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <Button
              variant="primary"
              className="btn btn-primary"
              onClick={handleCandidaterClick}
            >
              <FaPaperPlane />
              Candidater
            </Button>
            <Button
              variant="secondary"
              className="btn btn-secondary"
              onClick={handleDownloadClick}
            >
              <FaDownload />
              Télécharger la brochure
            </Button>
          </motion.div>
        </motion.div>

        <motion.div className="video-section" variants={itemVariants}>
          <motion.button
            className="watch-video-btn"
            onClick={() => setShowVideo(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPlay /> Regarder la vidéo de présentation
          </motion.button>
        </motion.div>

        <AnimatePresence>
          {showVideo && (
            <>
              <motion.div
                className="isitn-video-modal-overlay"
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={() => setShowVideo(false)}
              />
              <motion.div
                className="isitn-video-modal"
                variants={videoModalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <motion.button
                  className="isitn-close-video-btn"
                  onClick={() => setShowVideo(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaTimes />
                </motion.button>
                <div className="isitn-video-container">
                  <YouTube videoId={videoId} opts={opts} />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
